import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, CalendarDays, Truck, MapPin, PackageCheck, ArrowLeft, Phone, Clock } from 'lucide-react';
type OrderStep = 'confirmed' | 'scheduled' | 'dispatched' | 'en_route' | 'delivered';
const STEPS = [{
  id: 'confirmed',
  label: 'Order Confirmed',
  icon: CheckCircle2,
  desc: 'We received your order.'
}, {
  id: 'scheduled',
  label: 'Scheduled',
  icon: CalendarDays,
  desc: 'Delivery window confirmed.'
}, {
  id: 'dispatched',
  label: 'Dispatched',
  icon: ClipboardCheck,
  desc: 'Driver assigned and loading.'
}, {
  id: 'en_route',
  label: 'En Route',
  icon: Truck,
  desc: 'Truck is on the way to your site.'
}, {
  id: 'delivered',
  label: 'Delivered',
  icon: PackageCheck,
  desc: 'Material successfully dumped.'
}];
// Need to import ClipboardCheck locally since it's not in the main import list above
import { ClipboardCheck } from 'lucide-react';
export function TrackOrder() {
  const {
    orderId
  } = useParams<{
    orderId: string;
  }>();
  const [orderData, setOrderData] = useState<any>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(1); // Start at 'scheduled'
  useEffect(() => {
    window.scrollTo(0, 0);
    const saved = localStorage.getItem('lastOrder');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.orderId === orderId) {
          setOrderData(parsed);
        }
      } catch (e) {
        console.error('Could not parse order data');
      }
    }
    // Simulate progression for demo purposes
    const timer1 = setTimeout(() => setCurrentStepIndex(2), 6000); // Dispatched
    const timer2 = setTimeout(() => setCurrentStepIndex(3), 12000); // En Route
    const timer3 = setTimeout(() => setCurrentStepIndex(4), 18000); // Delivered
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [orderId]);
  if (!orderData) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 bg-[#fdfbf7] dark:bg-[#1b1b1b]" data-id="element-2288">
        <h1 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-2289">
          Order Not Found
        </h1>
        <p className="text-stone-500 mb-6" data-id="element-2290">
          We couldn't find tracking details for order {orderId}.
        </p>
        <Link to="/" className="text-brand-orange hover:underline font-medium" data-id="element-2291">
          Return to Home
        </Link>
      </div>;
  }
  return <main className="py-12 md:py-20 bg-[#fdfbf7] dark:bg-[#1b1b1b] min-h-[calc(100vh-64px)]" data-id="element-2292">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2293">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-brand-muted hover:text-brand-orange transition-colors mb-8" data-id="element-2294">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2295" />
          Back to Home
        </Link>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4" data-id="element-2296">
          <div data-id="element-2297">
            <h1 className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white" data-id="element-2298">
              Track Order
            </h1>
            <p className="text-stone-500 dark:text-stone-400 mt-1" data-id="element-2299">
              Order #{orderData.orderId}
            </p>
          </div>
          {currentStepIndex < 4 && <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-brand-orange/10 border border-brand-orange/20 text-brand-orange text-sm font-bold" data-id="element-2300">
              <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse mr-2" data-id="element-2301" />
              You're #2 in the queue
            </div>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2302">
          {/* Left Col: Map & Summary */}
          <div className="md:col-span-2 space-y-6" data-id="element-2303">
            {/* Mock Map */}
            <div className="bg-white dark:bg-[#222] rounded-2xl border border-stone-200 dark:border-[#333] overflow-hidden shadow-sm h-64 relative" data-id="element-2304">
              <div className="absolute inset-0 bg-gradient-to-br from-stone-100 to-stone-200 dark:from-[#2a2a2a] dark:to-[#1a1a1a]" data-id="element-2305" />
              {/* Fake route line */}
              <svg className="absolute inset-0 w-full h-full opacity-30 dark:opacity-20" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-2306">
                <path d="M10,90 Q30,50 50,50 T90,10" fill="none" stroke="#1DB954" strokeWidth="2" strokeDasharray="4 4" data-id="element-2307" />
              </svg>
              {/* Fake pins */}
              <div className="absolute bottom-4 left-4 w-4 h-4 bg-brand-charcoal dark:bg-white rounded-full border-2 border-white dark:border-black shadow-md" data-id="element-2308" />
              <div className="absolute top-4 right-4 w-4 h-4 bg-brand-orange rounded-full border-2 border-white dark:border-black shadow-md" data-id="element-2309" />

              {/* Animated Truck */}
              {currentStepIndex >= 2 && currentStepIndex < 4 && <motion.div className="absolute z-10 bg-white dark:bg-black p-2 rounded-full shadow-lg border border-stone-200 dark:border-[#444]" initial={{
              left: '10%',
              top: '80%'
            }} animate={{
              left: currentStepIndex === 3 ? '50%' : '20%',
              top: currentStepIndex === 3 ? '50%' : '70%'
            }} transition={{
              duration: 2,
              ease: 'easeInOut'
            }} data-id="element-2310">
                  <Truck className="w-5 h-5 text-[#1DB954]" data-id="element-2311" />
                </motion.div>}

              <div className="absolute top-4 left-4 bg-white/90 dark:bg-black/80 backdrop-blur-sm px-4 py-2 rounded-lg border border-stone-200 dark:border-[#444] shadow-sm" data-id="element-2312">
                <p className="text-xs font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-0.5" data-id="element-2313">
                  Live ETA
                </p>
                <p className="text-lg font-bold text-brand-charcoal dark:text-white" data-id="element-2314">
                  {currentStepIndex === 4 ? 'Delivered' : '45 mins'}
                </p>
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-white dark:bg-[#222] rounded-2xl border border-stone-200 dark:border-[#333] p-6 shadow-sm" data-id="element-2315">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-4 flex items-center" data-id="element-2316">
                <FileText className="w-5 h-5 mr-2 text-brand-orange" data-id="element-2317" />
                Order Details
              </h3>

              <div className="space-y-4" data-id="element-2318">
                <div className="flex items-start gap-3" data-id="element-2319">
                  <MapPin className="w-5 h-5 text-stone-400 shrink-0 mt-0.5" data-id="element-2320" />
                  <div data-id="element-2321">
                    <p className="text-sm font-medium text-stone-900 dark:text-white" data-id="element-2322">
                      {orderData.address}
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5" data-id="element-2323">
                      Instructions: {orderData.instructions || 'None'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3" data-id="element-2324">
                  <Clock className="w-5 h-5 text-stone-400 shrink-0 mt-0.5" data-id="element-2325" />
                  <div data-id="element-2326">
                    <p className="text-sm font-medium text-stone-900 dark:text-white" data-id="element-2327">
                      {new Date(orderData.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'short',
                      day: 'numeric'
                    })}
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5" data-id="element-2328">
                      Window: {orderData.time || 'Any time'}
                    </p>
                  </div>
                </div>

                <div className="border-t border-stone-100 dark:border-[#333] pt-4 mt-4" data-id="element-2329">
                  {orderData.items.map((item: any, i: number) => <div key={i} className="flex justify-between text-sm mb-2" data-id="element-2330">
                      <span className="text-stone-700 dark:text-stone-300" data-id="element-2331">
                        {item.quantity} yds {item.product}
                      </span>
                      <span className="font-medium text-stone-900 dark:text-white" data-id="element-2332">
                        ${(item.quantity * item.pricePerYard).toFixed(2)}
                      </span>
                    </div>)}
                  <div className="flex justify-between text-sm mb-2 text-stone-500" data-id="element-2333">
                    <span data-id="element-2334">Delivery Fee</span>
                    <span data-id="element-2335">${orderData.totalDeliveryFee.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2 text-stone-500" data-id="element-2336">
                    <span data-id="element-2337">Tax</span>
                    <span data-id="element-2338">${orderData.estimatedTax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg mt-4 pt-4 border-t border-stone-100 dark:border-[#333] text-brand-charcoal dark:text-white" data-id="element-2339">
                    <span data-id="element-2340">Total</span>
                    <span data-id="element-2341">${orderData.finalTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Col: Timeline */}
          <div className="bg-white dark:bg-[#222] rounded-2xl border border-stone-200 dark:border-[#333] p-6 shadow-sm h-fit" data-id="element-2342">
            <h3 className="font-bold text-brand-charcoal dark:text-white mb-6" data-id="element-2343">
              Status
            </h3>

            <div className="relative" data-id="element-2344">
              {/* Vertical line */}
              <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-stone-100 dark:bg-[#333]" data-id="element-2345" />

              <div className="space-y-8 relative" data-id="element-2346">
                {STEPS.map((step, index) => {
                const isPast = index < currentStepIndex;
                const isCurrent = index === currentStepIndex;
                const isFuture = index > currentStepIndex;
                const Icon = step.icon;
                return <div key={step.id} className="flex items-start gap-4" data-id="element-2347">
                      <div className="relative z-10 bg-white dark:bg-[#222] py-1" data-id="element-2348">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors duration-500 ${isPast ? 'bg-[#1DB954] border-[#1DB954] text-white' : isCurrent ? 'bg-white dark:bg-[#222] border-[#1DB954] text-[#1DB954]' : 'bg-white dark:bg-[#222] border-stone-200 dark:border-[#444] text-stone-300 dark:text-[#555]'}`} data-id="element-2349">
                          <Icon className="w-4 h-4" data-id="element-2350" />
                        </div>
                        {isCurrent && <span className="absolute inset-0 rounded-full border-2 border-[#1DB954] animate-ping opacity-20" style={{
                      top: '4px',
                      bottom: '4px'
                    }} data-id="element-2351" />}
                      </div>

                      <div className="pt-1.5" data-id="element-2352">
                        <p className={`text-sm font-bold transition-colors duration-500 ${isPast || isCurrent ? 'text-stone-900 dark:text-white' : 'text-stone-400 dark:text-[#666]'}`} data-id="element-2353">
                          {step.label}
                        </p>
                        <p className={`text-xs mt-0.5 transition-colors duration-500 ${isPast || isCurrent ? 'text-stone-500 dark:text-stone-400' : 'text-stone-300 dark:text-[#555]'}`} data-id="element-2354">
                          {step.desc}
                        </p>
                        {isPast && <p className="text-[10px] text-stone-400 mt-1" data-id="element-2355">
                            {new Date(Date.now() - (4 - index) * 600000).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                          </p>}
                      </div>
                    </div>;
              })}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-stone-100 dark:border-[#333]" data-id="element-2356">
              <p className="text-xs text-stone-500 mb-3" data-id="element-2357">
                Need help with this order?
              </p>
              <a href="tel:206-619-3742" className="flex items-center justify-center w-full py-2.5 rounded-lg border border-stone-200 dark:border-[#444] text-sm font-medium text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-[#2a2a2a] transition-colors" data-id="element-2358">
                <Phone className="w-4 h-4 mr-2" data-id="element-2359" />
                Call Dispatch
              </a>
            </div>
          </div>
        </div>
      </div>
    </main>;
}